class DTCCartSavings {
  constructor() {
    this.productCache = new Map();
    this.rootEl = null;
    this.signature = '';
    this.lastSavings = 0;
    this.updateToken = 0;
    this.debounceId = null;

    this.handleCartEvent = () => this.scheduleUpdate();
    this.handleDomMutation = () => {
      if (this.lastSavings <= 0) return;
      if (this.rootEl?.isConnected) return;
      if (!document.querySelector('[data-rebuy-component="cart-subtotal"]')) return;
      this.scheduleUpdate();
    };

    this.init();
  }

  init() {
    ['rebuy:smartcart.ready', 'rebuy:smartcart.show', 'rebuy:cart.ready', 'rebuy:cart.change'].forEach((name) => {
      document.addEventListener(name, this.handleCartEvent);
    });

    // Rebuy re-renders the drawer footer (Vue) and can drop injected nodes — re-insert when that happens
    this.observer = new MutationObserver(this.handleDomMutation);
    this.observer.observe(document.body, { childList: true, subtree: true });

    this.scheduleUpdate();
  }

  scheduleUpdate() {
    clearTimeout(this.debounceId);
    this.debounceId = setTimeout(() => this.update(), 60);
  }

  async update() {
    const token = ++this.updateToken;
    const totals = await this.computeTotals();
    if (token !== this.updateToken || !totals) return;
    this.render(totals);
  }

  getProduct(handle) {
    if (!this.productCache.has(handle)) {
      const request = fetch(`/products/${handle}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
      this.productCache.set(handle, request);
    }
    return this.productCache.get(handle);
  }

  async computeTotals() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return null;
      const cart = await response.json();
      if (!cart.items?.length) return { savings: 0, totalBefore: 0 };

      // compare_at_price is not exposed by /cart.js — resolve it per variant from the product JSON
      const products = await Promise.all(cart.items.map((item) => this.getProduct(item.handle)));

      let totalBefore = 0;
      let totalFinal = 0;

      cart.items.forEach((item, index) => {
        const variant = products[index]?.variants?.find(({ id }) => id === item.variant_id);
        const compareAt = variant?.compare_at_price ?? 0;
        const base = compareAt > item.price ? compareAt : item.price;
        totalBefore += base * item.quantity;
        totalFinal += item.final_line_price;
      });

      return { savings: totalBefore - totalFinal, totalBefore };
    } catch {
      return null;
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active ?? 'USD',
    }).format(cents / 100);
  }

  render({ savings, totalBefore }) {
    this.lastSavings = savings;

    const container = document.querySelector('[data-rebuy-component="cart-subtotal"]');
    if (!container) return;

    if (savings <= 0) {
      this.rootEl?.remove();
      this.rootEl = null;
      this.signature = '';
      return;
    }

    const signature = `${totalBefore}:${savings}`;
    if (this.rootEl?.isConnected && this.signature === signature && this.rootEl.parentElement === container) return;

    if (!this.rootEl) {
      this.rootEl = document.createElement('div');
      this.rootEl.className = 'dtc-cart-savings';
      this.rootEl.setAttribute('aria-live', 'polite');
    }

    this.rootEl.innerHTML = `
      <div class="dtc-cart-savings__row dtc-cart-savings__total-row">
        <span class="dtc-cart-savings__total-label">Total</span>
        <span class="dtc-cart-savings__total-amount">${this.formatMoney(totalBefore)}</span>
      </div>
      <div class="dtc-cart-savings__row dtc-cart-savings__saving-row">
        <span class="dtc-cart-savings__saving-label">Saving</span>
        <span class="dtc-cart-savings__saving-amount">${this.formatMoney(savings)}</span>
      </div>
    `;
    this.signature = signature;

    if (container.firstElementChild !== this.rootEl) {
      container.prepend(this.rootEl);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new DTCCartSavings(), { once: true });
} else {
  new DTCCartSavings();
}
